import {
  conditionName,
  gridDistance,
  rulerDistance,
  snapMeasurePoint,
  templateShape,
  type MapTemplate,
  type TemplateKind,
  linkKey,
  pinLabelFromTitle,
  snapToGrid,
  TOKEN_COLORS,
  type FogShape,
  type FogState,
  type MapDTO,
  type MapGrid,
  type MapPinDTO,
  type MapTokenDTO,
  type NoteDetail,
} from '@kartothek/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type Konva from 'konva';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Arc, Circle, Group, Image as KImage, Layer, Line, Rect, Shape, Stage, Text } from 'react-konva';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useNote, useTypes } from '../hooks';
import { t } from '../i18n';
import { classNames } from '../util';
import { Combobox, useNoteOptions } from './Combobox';
import { imageTarget, ImagePicker } from './ImagePicker';
import { MarkdownView } from './MarkdownView';
import { NoteLink } from './NoteLink';

type Tool = 'select' | 'pin' | 'token' | 'fog' | 'measure';
type MeasureKind = 'ruler' | TemplateKind;
interface View {
  scale: number;
  x: number;
  y: number;
}

const PIN_R = 13;
const MIN_SCALE = 0.05;
const MAX_SCALE = 8;

/** URL des Kartenbilds aus dem Frontmatter (`image: "[[Karte.jpg]]"`), über die aufgelösten Links der Notiz. */
export function mapImageUrl(note: NoteDetail): string | null {
  const target = imageTarget(note.frontmatter.image);
  if (!target) return null;
  const link = note.links[linkKey(target)];
  return link?.type === 'file' ? link.url : null;
}

function useHtmlImage(url: string | null) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setImg(null);
    setFailed(false);
    if (!url) return;
    const el = new window.Image();
    el.onload = () => setImg(el);
    el.onerror = () => setFailed(true);
    el.src = url;
    return () => {
      el.onload = null;
      el.onerror = null;
    };
  }, [url]);
  return { img, failed };
}

function useElementSize(ref: React.RefObject<HTMLElement | null>) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setSize({ width: Math.round(e!.contentRect.width), height: Math.round(e!.contentRect.height) }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

/** Raster als ein einziger Pfad – auch Tausende Hexfelder bleiben beim Verschieben flüssig. */
export function drawGrid(ctx: Konva.Context, grid: MapGrid, width: number, height: number) {
  const s = grid.size;
  ctx.beginPath();
  if (grid.type === 'square') {
    const x0 = (((grid.offsetX % s) + s) % s);
    const y0 = (((grid.offsetY % s) + s) % s);
    for (let x = x0; x <= width; x += s) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
    }
    for (let y = y0; y <= height; y += s) {
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
    }
  } else if (grid.type === 'hex') {
    // Spitze oben: Breite = Feldgröße, Radius = Breite / √3, Zeilenabstand = 1,5 × Radius
    const w = s;
    const r = w / Math.sqrt(3);
    const rowH = 1.5 * r;
    const startRow = Math.floor((0 - grid.offsetY) / rowH) - 1;
    const endRow = Math.ceil((height - grid.offsetY) / rowH) + 1;
    const startCol = Math.floor((0 - grid.offsetX) / w) - 1;
    const endCol = Math.ceil((width - grid.offsetX) / w) + 1;
    for (let row = startRow; row <= endRow; row++) {
      const cy = grid.offsetY + row * rowH;
      const shift = row % 2 !== 0 ? w / 2 : 0;
      for (let col = startCol; col <= endCol; col++) {
        const cx = grid.offsetX + col * w + shift;
        // Nur drei Kanten je Feld zeichnen, die übrigen zeichnen die Nachbarn
        ctx.moveTo(cx - w / 2, cy + r / 2);
        ctx.lineTo(cx - w / 2, cy - r / 2);
        ctx.lineTo(cx, cy - r);
        ctx.lineTo(cx + w / 2, cy - r / 2);
      }
    }
  }
}

function GridSettings(props: { grid: MapGrid; onChange: (patch: Partial<MapGrid>) => void }) {
  const { grid } = props;
  const num = (key: keyof MapGrid, label: string, step = 1) => (
    <label className="sb-field">
      <span>{label}</span>
      <input
        className="sb-num"
        type="number"
        step={step}
        value={grid[key] as number}
        onChange={(e) => e.target.value !== '' && props.onChange({ [key]: Number(e.target.value) })}
      />
    </label>
  );
  return (
    <div className="map-panel-section">
      <p className="small muted">{t('map.gridHint')}</p>
      <label className="sb-field">
        <span>{t('map.gridType')}</span>
        <select value={grid.type} onChange={(e) => props.onChange({ type: e.target.value as MapGrid['type'] })}>
          <option value="none">{t('map.gridNone')}</option>
          <option value="square">{t('map.gridSquare')}</option>
          <option value="hex">{t('map.gridHex')}</option>
        </select>
      </label>
      {grid.type !== 'none' && (
        <>
          {num('size', t('map.gridSize'), 0.5)}
          {num('offsetX', t('map.gridOffsetX'), 0.5)}
          {num('offsetY', t('map.gridOffsetY'), 0.5)}
          <label className="sb-field">
            <span>{t('map.gridScale')}</span>
            <span className="row">
              <input className="sb-num" type="number" step={0.5} value={grid.cellDistance} onChange={(e) => props.onChange({ cellDistance: Number(e.target.value) || 0 })} />
              <input className="sb-num" value={grid.cellUnit} onChange={(e) => props.onChange({ cellUnit: e.target.value.slice(0, 20) })} />
            </span>
          </label>
          <label className="sb-field">
            <span>{t('map.gridColor')}</span>
            <input type="color" value={grid.color} onChange={(e) => props.onChange({ color: e.target.value })} />
          </label>
          <label className="sb-field">
            <span>{t('map.gridOpacity')}</span>
            <input type="range" min={0.05} max={1} step={0.05} value={grid.opacity} onChange={(e) => props.onChange({ opacity: Number(e.target.value) })} />
          </label>
        </>
      )}
    </div>
  );
}

/** Fläche (Kegel/Kreis/Quadrat) mit Größenangabe – Schrift bleibt beim Zoomen gleich groß. */
export function TemplateNode(props: {
  template: Pick<MapTemplate, 'kind' | 'x' | 'y' | 'x2' | 'y2' | 'color'>;
  grid: MapGrid;
  scale: number;
  selected?: boolean;
  onSelect?: () => void;
}) {
  const { template: t, grid } = props;
  const shape = templateShape(grid, t);
  const inv = 1 / props.scale;
  const label = `${t.kind === 'circle' ? 'r ' : ''}${shape.distance} ${grid.cellUnit}`;
  const common = {
    fill: t.color,
    opacity: 0.32,
    listening: !!props.onSelect,
    onClick: (e: Konva.KonvaEventObject<MouseEvent>) => {
      e.cancelBubble = true;
      props.onSelect?.();
    },
    onTap: (e: Konva.KonvaEventObject<TouchEvent>) => {
      e.cancelBubble = true;
      props.onSelect?.();
    },
  };
  const strokeProps = { stroke: t.color, strokeWidth: (props.selected ? 4 : 2) * inv, listening: false, dash: props.selected ? [8 * inv, 5 * inv] : undefined };
  const labelPos =
    t.kind === 'circle'
      ? { x: t.x, y: t.y }
      : t.kind === 'square'
        ? { x: (shape.points[0]! + shape.points[4]!) / 2, y: (shape.points[1]! + shape.points[5]!) / 2 }
        : { x: (shape.points[2]! + shape.points[4]!) / 2, y: (shape.points[3]! + shape.points[5]!) / 2 };
  return (
    <Group>
      {t.kind === 'circle' ? (
        <>
          <Circle x={t.x} y={t.y} radius={shape.radius} {...common} />
          <Circle x={t.x} y={t.y} radius={shape.radius} {...strokeProps} />
          <Circle x={t.x} y={t.y} radius={4 * inv} fill={t.color} listening={false} />
        </>
      ) : (
        <>
          <Line points={shape.points} closed {...common} />
          <Line points={shape.points} closed {...strokeProps} />
        </>
      )}
      <Text
        text={label}
        x={labelPos.x}
        y={labelPos.y}
        fontSize={16 * inv}
        fontStyle="bold"
        fill="#fff8e1"
        stroke="#1b1208"
        strokeWidth={4 * inv}
        fillAfterStrokeEnabled
        width={140 * inv}
        offsetX={70 * inv}
        offsetY={8 * inv}
        align="center"
        listening={false}
      />
    </Group>
  );
}

function RulerNode(props: { a: { x: number; y: number }; b: { x: number; y: number }; grid: MapGrid; scale: number }) {
  const inv = 1 / props.scale;
  const { a, b } = props;
  return (
    <Group listening={false}>
      <Line points={[a.x, a.y, b.x, b.y]} stroke="#1b1208" strokeWidth={6 * inv} lineCap="round" />
      <Line points={[a.x, a.y, b.x, b.y]} stroke="#f0c46c" strokeWidth={3 * inv} lineCap="round" dash={[10 * inv, 6 * inv]} />
      <Circle x={a.x} y={a.y} radius={5 * inv} fill="#f0c46c" stroke="#1b1208" strokeWidth={2 * inv} />
      <Circle x={b.x} y={b.y} radius={5 * inv} fill="#f0c46c" stroke="#1b1208" strokeWidth={2 * inv} />
      <Text
        text={`${rulerDistance(props.grid, a, b)} ${props.grid.cellUnit}`}
        x={b.x + 10 * inv}
        y={b.y - 26 * inv}
        fontSize={18 * inv}
        fontStyle="bold"
        fill="#f0c46c"
        stroke="#1b1208"
        strokeWidth={5 * inv}
        fillAfterStrokeEnabled
      />
    </Group>
  );
}

const TEMPLATE_COLORS = ['#e25822', '#3b82c4', '#3f9b5d', '#9b59b6', '#e8b04a'];

function MeasureSettings(props: {
  kind: MeasureKind;
  color: string;
  count: number;
  selected: boolean;
  onKind: (k: MeasureKind) => void;
  onColor: (c: string) => void;
  onDeleteSelected: () => void;
  onClear: () => void;
}) {
  const btn = (k: MeasureKind, label: string) => (
    <button className={classNames(props.kind === k && 'on')} onClick={() => props.onKind(k)}>
      {label}
    </button>
  );
  return (
    <div className="map-panel-section">
      <p className="small muted">{t('map.measureHint')}</p>
      <div className="row">
        {btn('ruler', `📏 ${t('map.ruler')}`)}
        {btn('cone', `◣ ${t('map.cone')}`)}
        {btn('circle', `◯ ${t('map.circle')}`)}
        {btn('square', `▢ ${t('map.square')}`)}
      </div>
      {props.kind !== 'ruler' && (
        <div className="row template-colors">
          {TEMPLATE_COLORS.map((c) => (
            <button key={c} className={classNames('swatch', props.color === c && 'on')} style={{ background: c }} onClick={() => props.onColor(c)} aria-label={c} />
          ))}
        </div>
      )}
      <p className="small muted">{t('map.templatesInfo', { n: props.count })}</p>
      <div className="row">
        {props.selected && (
          <button className="danger" onClick={props.onDeleteSelected}>
            {t('map.templateDelete')}
          </button>
        )}
        <button disabled={props.count === 0} onClick={props.onClear}>
          {t('map.templatesClear')}
        </button>
      </div>
    </div>
  );
}

const FOG_COLOR = '#0b0805';
/** Für den DM bläulich, damit sich Nebel von dunklen Kartenbereichen abhebt. */
const FOG_COLOR_DM = '#1c1a3a';

function FogShapeNode({ shape, color }: { shape: FogShape; color: string }) {
  const op = shape.hide ? 'source-over' : 'destination-out';
  if (shape.type === 'rect') return <Rect x={shape.x} y={shape.y} width={shape.w} height={shape.h} fill={color} globalCompositeOperation={op} />;
  if (shape.type === 'poly') return <Line points={shape.points} closed fill={color} globalCompositeOperation={op} />;
  return (
    <Line
      points={shape.points.length === 2 ? [...shape.points, ...shape.points] : shape.points}
      stroke={color}
      strokeWidth={shape.width}
      lineCap="round"
      lineJoin="round"
      globalCompositeOperation={op}
    />
  );
}

/** Nebel: alles verdeckt, dann Schritte der Reihe nach (Aufdecken stanzt aus, Verdecken malt wieder zu). */
export function FogLayer(props: { fog: FogState; draft: FogShape | null; width: number; height: number; gm: boolean }) {
  const color = props.gm ? FOG_COLOR_DM : FOG_COLOR;
  return (
    <Layer listening={false} opacity={props.gm ? 0.62 : 1}>
      <Rect x={-props.width} y={-props.height} width={props.width * 3} height={props.height * 3} fill={color} />
      {props.fog.revealed.map((shape, i) => (
        <FogShapeNode key={i} shape={shape} color={color} />
      ))}
      {props.draft && <FogShapeNode shape={props.draft} color={color} />}
    </Layer>
  );
}

type FogMode = { hide: boolean; shape: 'rect' | 'brush' };

function FogSettings(props: {
  fog: FogState;
  mode: FogMode;
  brush: number;
  onMode: (m: FogMode) => void;
  onBrush: (n: number) => void;
  onChange: (fog: FogState) => void;
  imageSize: { width: number; height: number };
}) {
  const { fog, mode } = props;
  const btn = (m: FogMode, label: string) => (
    <button className={classNames(mode.hide === m.hide && mode.shape === m.shape && 'on')} onClick={() => props.onMode(m)}>
      {label}
    </button>
  );
  return (
    <div className="map-panel-section">
      <label className="inline">
        <input type="checkbox" checked={fog.enabled} onChange={(e) => props.onChange({ ...fog, enabled: e.target.checked })} /> {t('map.fogEnabled')}
      </label>
      <p className="small muted">{t('map.fogHint')}</p>
      <div className="row">
        {btn({ hide: false, shape: 'rect' }, `▭ ${t('map.fogRevealRect')}`)}
        {btn({ hide: false, shape: 'brush' }, `🖌 ${t('map.fogRevealBrush')}`)}
        {btn({ hide: true, shape: 'rect' }, `▬ ${t('map.fogHideRect')}`)}
        {btn({ hide: true, shape: 'brush' }, `🖌 ${t('map.fogHideBrush')}`)}
      </div>
      {mode.shape === 'brush' && (
        <label className="sb-field">
          <span>{t('map.fogBrush')}</span>
          <input type="range" min={10} max={200} step={5} value={props.brush} onChange={(e) => props.onBrush(Number(e.target.value))} />
        </label>
      )}
      <div className="row">
        <button disabled={fog.revealed.length === 0} onClick={() => props.onChange({ ...fog, revealed: fog.revealed.slice(0, -1) })}>
          ↶ {t('map.fogUndo')}
        </button>
        <button onClick={() => props.onChange({ enabled: true, revealed: [{ type: 'rect', x: 0, y: 0, w: props.imageSize.width, h: props.imageSize.height }] })}>
          {t('map.fogRevealAll')}
        </button>
        <button
          className="danger"
          onClick={() => {
            if (confirm(t('map.fogResetConfirm'))) props.onChange({ enabled: true, revealed: [] });
          }}
        >
          {t('map.fogReset')}
        </button>
      </div>
    </div>
  );
}

const tokenImages = new Map<string, HTMLImageElement>();

function useTokenImage(url: string | null): HTMLImageElement | null {
  const [img, setImg] = useState<HTMLImageElement | null>(() => (url ? (tokenImages.get(url)?.complete ? tokenImages.get(url)! : null) : null));
  useEffect(() => {
    if (!url) return setImg(null);
    let el = tokenImages.get(url);
    if (!el) {
      el = new window.Image();
      el.src = url;
      tokenImages.set(url, el);
    }
    if (el.complete && el.naturalWidth) return setImg(el);
    const done = () => setImg(el!);
    el.addEventListener('load', done);
    return () => el!.removeEventListener('load', done);
  }, [url]);
  return img;
}

/** „Oger 2“ → „O2“, „Ser Albrecht“ → „SA“, „Lena“ → „Le“. */
function initials(name: string): string {
  const num = /\s(\d+)$/.exec(name);
  const words = name.replace(/\s\d+$/, '').split(/\s+/).filter(Boolean);
  if (num) return `${(words[0] ?? '?')[0]!.toUpperCase()}${num[1]}`;
  if (words.length > 1) return `${words[0]![0]}${words[1]![0]}`.toUpperCase();
  return (words[0] ?? '?').slice(0, 2);
}

export function TokenNode(props: {
  token: MapTokenDTO;
  grid: MapGrid;
  cell: number;
  scale: number;
  selected: boolean;
  draggable: boolean;
  onSelect: () => void;
  onMove: (pos: { x: number; y: number }) => void;
}) {
  const { token, grid, cell } = props;
  const r = Math.max(4, (token.size * cell) / 2 - 2);
  const cb = token.combat;
  const hp = cb && cb.hpMax ? Math.max(0, Math.min(1, (cb.hpCurrent ?? cb.hpMax) / cb.hpMax)) : null;
  const ring = hp === null ? null : hp > 0.5 ? '#5fb85f' : hp > 0.25 ? '#e0b030' : '#d04030';
  const out = !!cb && (cb.dead || cb.defeated);
  const [drag, setDrag] = useState<{ x: number; y: number; dist: number } | null>(null);
  const picture = useTokenImage(token.imageUrl);
  const inv = 1 / props.scale;
  const nameSize = Math.max(11 * inv, r * 0.42);
  // Bei kleinem Zoom überlappen die Namen – dann reichen die Kürzel
  const showName = props.selected || cb?.active || r * props.scale >= 18;
  return (
    <Group
      x={token.x}
      y={token.y}
      draggable={props.draggable}
      opacity={out ? 0.55 : token.hidden ? 0.65 : 1}
      onClick={(e) => {
        e.cancelBubble = true;
        props.onSelect();
      }}
      onTap={(e) => {
        e.cancelBubble = true;
        props.onSelect();
      }}
      onMouseEnter={(e) => {
        const c = e.target.getStage()?.container();
        if (c && props.draggable) c.style.cursor = 'move';
      }}
      onMouseLeave={(e) => {
        const c = e.target.getStage()?.container();
        if (c) c.style.cursor = '';
      }}
      onDragStart={(e) => {
        e.cancelBubble = true;
        setDrag({ x: token.x, y: token.y, dist: 0 });
      }}
      onDragMove={(e) => {
        e.cancelBubble = true;
        const pos = snapToGrid(grid, e.target.x(), e.target.y(), token.size);
        setDrag((d) => (d ? { ...d, dist: gridDistance(grid, d, pos) } : d));
      }}
      onDragEnd={(e) => {
        e.cancelBubble = true;
        const pos = snapToGrid(grid, e.target.x(), e.target.y(), token.size);
        e.target.position(pos);
        setDrag(null);
        props.onMove(pos);
      }}
    >
      {cb?.active && <Circle radius={r * 1.22} stroke="#f0c46c" strokeWidth={Math.max(2 * inv, r * 0.14)} shadowColor="#f0c46c" shadowBlur={18} shadowOpacity={0.9} />}
      <Circle
        radius={r}
        fill={token.color}
        stroke={props.selected ? '#fff8e1' : '#1b1208'}
        strokeWidth={Math.max(1.5 * inv, r * (props.selected ? 0.12 : 0.07))}
        dash={token.hidden ? [r * 0.35, r * 0.2] : undefined}
        shadowColor="#000"
        shadowBlur={r * 0.4}
        shadowOpacity={0.6}
      />
      {picture && (
        // Bild im Kreis, Rand in der Farbe der Art (SC blau, Monster rot …)
        <Circle
          radius={r * 0.86}
          fillPatternImage={picture}
          fillPatternRepeat="no-repeat"
          fillPatternScaleX={(r * 1.72) / Math.min(picture.width, picture.height)}
          fillPatternScaleY={(r * 1.72) / Math.min(picture.width, picture.height)}
          fillPatternOffsetX={picture.width / 2}
          fillPatternOffsetY={picture.height / 2}
          listening={false}
        />
      )}
      {ring && hp !== null && hp > 0 && <Arc innerRadius={r * 0.84} outerRadius={r} angle={360 * hp} rotation={-90} fill={ring} listening={false} />}
      {!picture && <Text
        text={initials(token.name)}
        fontSize={r * 0.75}
        fontStyle="bold"
        fill="#fff8e1"
        width={r * 2}
        height={r * 2}
        offsetX={r}
        offsetY={r}
        align="center"
        verticalAlign="middle"
        listening={false}
      />}
      {out && (
        <Text text={cb!.dead ? '✝' : '✕'} fontSize={r * 1.5} fill="rgba(20,10,5,0.85)" width={r * 2} height={r * 2} offsetX={r} offsetY={r} align="center" verticalAlign="middle" listening={false} />
      )}
      {showName && <Text
        text={token.name}
        y={r + 2 * inv}
        fontSize={nameSize}
        fontStyle="bold"
        fill="#fff8e1"
        stroke="#1b1208"
        strokeWidth={nameSize * 0.22}
        fillAfterStrokeEnabled
        width={nameSize * 14}
        offsetX={nameSize * 7}
        align="center"
        listening={false}
      />}
      {drag && drag.dist > 0 && (
        <Text
          text={`${+(drag.dist * grid.cellDistance).toFixed(1)} ${grid.cellUnit}`}
          y={-r - 22 * inv}
          fontSize={15 * inv}
          fontStyle="bold"
          fill="#f0c46c"
          stroke="#1b1208"
          strokeWidth={4 * inv}
          fillAfterStrokeEnabled
          width={120 * inv}
          offsetX={60 * inv}
          align="center"
          listening={false}
        />
      )}
    </Group>
  );
}

const TOKEN_SIZES = [0.5, 1, 2, 3, 4];

function TokenPanel(props: { cid: string; token: MapTokenDTO; onPatch: (patch: Parameters<typeof api.updateToken>[3]) => void; onDelete: () => void }) {
  const { token } = props;
  const cb = token.combat;
  const [name, setName] = useState(token.name);
  useEffect(() => setName(token.name), [token.id, token.name]);
  return (
    <div className="map-panel-section">
      <h3>
        {token.noteId ? (
          <NoteLink cid={props.cid} noteId={token.noteId} preview={false}>
            {token.name}
          </NoteLink>
        ) : (
          token.name
        )}
      </h3>
      {cb && (
        <div className="small">
          {cb.hpMax !== null && (
            <div>
              {t('map.tokenHp')}: <strong>{cb.hpCurrent ?? cb.hpMax}</strong>/{cb.hpMax}
              {cb.tempHp > 0 && <span className="temp-badge">+{cb.tempHp}</span>}
            </div>
          )}
          {cb.dead ? <span className="dead-badge">✝ {t('combat.dead')}</span> : cb.defeated ? <span className="muted">{t('combat.defeated')}</span> : null}
          {cb.conditions.length > 0 && <div className="muted">{cb.conditions.map(conditionName).join(', ')}</div>}
          <div className="muted">{t('map.tokenCombatHint')}</div>
        </div>
      )}
      {!token.combatantId && (
        <label className="sb-field">
          <span>{t('common.name')}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => name.trim() && name.trim() !== token.name && props.onPatch({ name: name.trim() })}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          />
        </label>
      )}
      {token.noteId ? (
        <p className="small muted">{t('map.tokenImageFromNote')}</p>
      ) : (
        <label className="sb-field">
          <span>{t('map.tokenImage')}</span>
          <ImagePicker cid={props.cid} value={token.image ?? ''} onChange={(v) => props.onPatch({ image: v || null })} />
        </label>
      )}
      <label className="sb-field">
        <span>{t('map.tokenSize')}</span>
        <select value={token.size} onChange={(e) => props.onPatch({ size: Number(e.target.value) })}>
          {TOKEN_SIZES.map((n) => (
            <option key={n} value={n}>
              {n === 0.5 ? '½' : n} × {n === 0.5 ? '½' : n}
            </option>
          ))}
        </select>
      </label>
      <label className="sb-field">
        <span>{t('map.gridColor')}</span>
        <input type="color" value={token.color} onChange={(e) => props.onPatch({ color: e.target.value })} />
      </label>
      <label className="inline">
        <input type="checkbox" checked={token.hidden} onChange={(e) => props.onPatch({ hidden: e.target.checked })} /> {t('map.tokenHidden')}
      </label>
      <div className="row">
        <button className="danger" onClick={props.onDelete}>
          {t('map.tokenDelete')}
        </button>
      </div>
    </div>
  );
}

function PinPanel(props: { cid: string; pin: MapPinDTO; onPatch: (patch: Parameters<typeof api.updatePin>[3]) => void; onDelete: () => void }) {
  const { pin } = props;
  const linked = useNote(props.cid, pin.noteId ?? undefined);
  const options = useNoteOptions(props.cid);
  const [link, setLink] = useState(pin.noteTitle ?? '');
  const [label, setLabel] = useState(pin.label);
  useEffect(() => {
    setLink(pin.noteTitle ?? '');
    setLabel(pin.label);
  }, [pin.id, pin.noteTitle, pin.label]);

  return (
    <div className="map-panel-section">
      <h3>{pin.noteId && pin.noteTitle ? (
        <NoteLink cid={props.cid} noteId={pin.noteId} preview={false}>
          {pin.noteTitle}
        </NoteLink>
      ) : (
        pin.label || t('map.noLink')
      )}</h3>
      <label className="sb-field">
        <span>{t('map.pinLink')}</span>
        <Combobox
          value={link}
          onChange={setLink}
          options={options}
          onPick={(o) => {
            setLink(o.title);
            props.onPatch({ noteId: o.key });
          }}
          onBlur={() => {
            if (!link.trim() && pin.noteId) props.onPatch({ noteId: null });
            else setLink(pin.noteTitle ?? '');
          }}
          placeholder={t('map.pinNote')}
        />
      </label>
      <label className="sb-field">
        <span>{t('map.pinLabel')}</span>
        <input
          value={label}
          placeholder={t('map.pinLabelHint')}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => label !== pin.label && props.onPatch({ label: label.trim() })}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        />
      </label>
      <label className="inline">
        <input type="checkbox" checked={pin.visibility === 'all'} onChange={(e) => props.onPatch({ visibility: e.target.checked ? 'all' : 'dm' })} />{' '}
        {t('map.pinVisible')}
      </label>
      <div className="row">
        {pin.noteId && (
          <Link className="button" to={`/c/${props.cid}/n/${pin.noteId}`}>
            {t('map.openNote')}
          </Link>
        )}
        <button className="danger" onClick={props.onDelete}>
          {t('map.pinDelete')}
        </button>
      </div>
      {linked.data && (
        <div className="map-pin-preview parchment">
          <MarkdownView note={linked.data} isGameMaster preview />
        </div>
      )}
    </div>
  );
}

/**
 * Karte einer Notiz vom Typ `karte`: Bild, Raster und Pins. Mausrad zoomt, Ziehen verschiebt,
 * Pins lassen sich (als DM) setzen, verschieben und mit Notizen verknüpfen.
 */
export function MapView(props: {
  note: NoteDetail;
  gm: boolean;
  fullscreen?: boolean;
  /** Im Kampf-Tracker: Token des gewählten Teilnehmers hervorheben. */
  highlightCombatantId?: string | null;
  onTokenSelect?: (token: MapTokenDTO) => void;
  /** Mitte des sichtbaren Ausschnitts in Bildkoordinaten (dort erscheinen neue Kampf-Tokens). */
  onViewCenter?: (center: { x: number; y: number }) => void;
  className?: string;
}) {
  const { note, gm } = props;
  const cid = note.campaignId;
  const qc = useQueryClient();
  const types = useTypes(cid);
  const url = mapImageUrl(note);
  const { img, failed } = useHtmlImage(url);
  const q = useQuery({ queryKey: ['map', cid, note.id], queryFn: () => api.map(cid, note.id) });
  const map = q.data;
  const wrapRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const size = useElementSize(wrapRef);
  const [view, setView] = useState<View>({ scale: 1, x: 0, y: 0 });
  const view_ = view;
  const [tool, setTool] = useState<Tool>('select');
  const [panel, setPanel] = useState<'grid' | 'fog' | 'measure' | null>(null);
  const [measureKind, setMeasureKind] = useState<MeasureKind>('ruler');
  const [templateColor, setTemplateColor] = useState(TEMPLATE_COLORS[0]!);
  const [measure, setMeasure] = useState<{ a: { x: number; y: number }; b: { x: number; y: number }; done: boolean } | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [fog, setFog] = useState<FogState | null>(null);
  const [fogMode, setFogMode] = useState<FogMode>({ hide: false, shape: 'rect' });
  const [brush, setBrush] = useState(60);
  const [fogDraft, setFogDraft] = useState<FogShape | null>(null);
  const fogStart = useRef<{ x: number; y: number } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedTokenId, setSelectedTokenId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ x: number; y: number; sx: number; sy: number } | null>(null);
  const [draftText, setDraftText] = useState('');
  const [hover, setHover] = useState<{ pin: MapPinDTO; sx: number; sy: number } | null>(null);
  const [grid, setGrid] = useState<MapGrid | null>(null);
  const [error, setError] = useState<string | null>(null);
  const gridTimer = useRef<number | undefined>(undefined);
  const fitted = useRef(false);
  const noteOptions = useNoteOptions(cid);
  const screen = useQuery({ queryKey: ['screen', cid], queryFn: () => api.screenSettings(cid), enabled: gm });
  const [screenMsg, setScreenMsg] = useState<string | null>(null);
  const creatureOptions = useNoteOptions(cid, ['nsc', 'monster', 'charakter']);

  useEffect(() => setGrid(map?.grid ?? null), [map?.grid]);
  useEffect(() => setFog(map?.fog ?? null), [map?.fog]);

  const setMap = useCallback((m: MapDTO) => qc.setQueryData(['map', cid, note.id], m), [qc, cid, note.id]);
  const run = useCallback(
    async (fn: () => Promise<MapDTO>) => {
      try {
        setError(null);
        setMap(await fn());
      } catch (e) {
        setError(e instanceof ApiError ? e.message : String(e));
      }
    },
    [setMap],
  );

  const fit = useCallback(() => {
    if (!img || !size.width || !size.height) return;
    const scale = Math.min(size.width / img.width, size.height / img.height) * 0.98;
    setView({ scale, x: (size.width - img.width * scale) / 2, y: (size.height - img.height * scale) / 2 });
  }, [img, size.width, size.height]);

  // Bildgröße einmalig melden (Platzierung neuer Tokens in der Bildmitte)
  useEffect(() => {
    if (!gm || !img || !map) return;
    if (map.image?.width === img.width && map.image?.height === img.height) return;
    void api.updateMap(cid, note.id, { image: { width: img.width, height: img.height } }).then(setMap, () => undefined);
  }, [gm, img, map, cid, note.id, setMap]);

  const onViewCenter = props.onViewCenter;
  useEffect(() => {
    if (!onViewCenter || !size.width) return;
    onViewCenter({ x: (size.width / 2 - view.x) / view.scale, y: (size.height / 2 - view.y) / view.scale });
  }, [onViewCenter, view, size.width, size.height]);

  useEffect(() => {
    if (img && size.width && !fitted.current) {
      fitted.current = true;
      fit();
    }
  }, [img, size.width, fit]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDraft(null);
        setMeasure(null);
        setTool('select');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    const pointer = stage?.getPointerPosition();
    if (!pointer) return;
    const factor = e.evt.deltaY > 0 ? 1 / 1.15 : 1.15;
    setView((v) => {
      const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale * factor));
      const px = (pointer.x - v.x) / v.scale;
      const py = (pointer.y - v.y) / v.scale;
      return { scale, x: pointer.x - px * scale, y: pointer.y - py * scale };
    });
    setDraft(null);
  };

  const onStageClick = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    if (e.target !== e.target.getStage() && e.target.name() !== 'map-image') return;
    setSelectedId(null);
    setSelectedTokenId(null);
    if (tool === 'select') setSelectedTemplateId(null);
    if (tool === 'select' || tool === 'fog' || tool === 'measure' || !gm) return;
    const pos = stageRef.current?.getRelativePointerPosition();
    const abs = stageRef.current?.getPointerPosition();
    if (!pos || !abs) return;
    setDraft({ x: pos.x, y: pos.y, sx: abs.x, sy: abs.y });
    setDraftText('');
  };

  const createPin = (noteId: string | null, label: string) => {
    if (!draft) return;
    const { x, y } = draft;
    setDraft(null);
    void run(async () => {
      const m = await api.createPin(cid, note.id, { x, y, noteId, label });
      setSelectedId(m.pins.find((p) => Math.abs(p.x - x) < 0.01 && Math.abs(p.y - y) < 0.01)?.id ?? null);
      return m;
    });
  };

  const createToken = (name: string, noteId: string | null, typeKey?: string) => {
    if (!draft || !map) return;
    const pos = snapToGrid(map.grid, draft.x, draft.y, 1);
    setDraft(null);
    const color = typeKey === 'charakter' ? TOKEN_COLORS.pc : typeKey === 'nsc' ? TOKEN_COLORS.npc : typeKey === 'monster' ? TOKEN_COLORS.monster : TOKEN_COLORS.other;
    void run(() => api.createToken(cid, note.id, { ...pos, name, noteId, color }));
  };

  const changeFog = (next: FogState) => {
    setFog(next);
    void run(() => api.updateMap(cid, note.id, { fog: next }));
  };

  const saveTemplates = (templates: MapTemplate[]) => void run(() => api.updateMap(cid, note.id, { templates }));

  const fogPointer = () => stageRef.current?.getRelativePointerPosition() ?? null;
  const onFogDown = () => {
    if (tool === 'measure' && map) {
      const p = fogPointer();
      if (!p) return;
      const a = snapMeasurePoint(grid ?? map.grid, p.x, p.y);
      setMeasure({ a, b: a, done: false });
      setSelectedTemplateId(null);
      return;
    }
    if (tool !== 'fog' || !gm) return;
    const p = fogPointer();
    if (!p) return;
    fogStart.current = p;
    setFogDraft(
      fogMode.shape === 'rect'
        ? { type: 'rect', x: p.x, y: p.y, w: 0, h: 0, ...(fogMode.hide ? { hide: true } : {}) }
        : { type: 'line', points: [p.x, p.y], width: brush / view.scale, ...(fogMode.hide ? { hide: true } : {}) },
    );
  };
  const onFogMove = () => {
    if (tool === 'measure' && measure && !measure.done && map) {
      const p = fogPointer();
      if (p) setMeasure({ ...measure, b: snapMeasurePoint(grid ?? map.grid, p.x, p.y) });
      return;
    }
    const start = fogStart.current;
    if (!start || !fogDraft) return;
    const p = fogPointer();
    if (!p) return;
    if (fogDraft.type === 'rect') {
      setFogDraft({ ...fogDraft, x: Math.min(start.x, p.x), y: Math.min(start.y, p.y), w: Math.abs(p.x - start.x), h: Math.abs(p.y - start.y) });
    } else if (fogDraft.type === 'line') {
      const pts = fogDraft.points;
      // Nur Punkte mit etwas Abstand speichern
      if (Math.hypot(p.x - pts[pts.length - 2]!, p.y - pts[pts.length - 1]!) > fogDraft.width / 6) setFogDraft({ ...fogDraft, points: [...pts, p.x, p.y] });
    }
  };
  const onFogUp = () => {
    if (tool === 'measure' && measure && !measure.done) {
      const { a, b } = measure;
      if (measureKind === 'ruler' || !map || !gm) {
        // Lineal bleibt stehen, bis neu gemessen wird (ein bloßer Klick misst nichts)
        setMeasure(a.x === b.x && a.y === b.y ? null : { a, b, done: true });
        return;
      }
      setMeasure(null);
      if (Math.hypot(b.x - a.x, b.y - a.y) < 1) return;
      const tpl: MapTemplate = { id: Math.random().toString(36).slice(2, 10), kind: measureKind, x: a.x, y: a.y, x2: b.x, y2: b.y, color: templateColor };
      saveTemplates([...map.templates, tpl]);
      return;
    }
    const draftShape = fogDraft;
    fogStart.current = null;
    setFogDraft(null);
    if (!draftShape || !fog) return;
    if (draftShape.type === 'rect' && (draftShape.w < 2 || draftShape.h < 2)) return;
    changeFog({ ...fog, enabled: true, revealed: [...fog.revealed, draftShape] });
  };

  /** Diese Karte mit dem aktuellen Ausschnitt in der Spieleransicht zeigen. */
  const showToPlayers = async () => {
    if (!size.width) return;
    const view = { x: -view_.x / view_.scale, y: -view_.y / view_.scale, w: size.width / view_.scale, h: size.height / view_.scale };
    try {
      const res = await api.updateScreen(cid, { mapNoteId: note.id, view });
      qc.setQueryData(['screen', cid], res);
      const link = `${location.origin}/screen/${res.token}`;
      try {
        await navigator.clipboard.writeText(link);
        setScreenMsg(t('map.shownCopied'));
      } catch {
        setScreenMsg(link);
      }
      window.setTimeout(() => setScreenMsg(null), 6000);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const changeGrid = (patch: Partial<MapGrid>) => {
    setGrid((g) => (g ? { ...g, ...patch } : g));
    window.clearTimeout(gridTimer.current);
    const next = { ...(grid ?? map!.grid), ...patch };
    gridTimer.current = window.setTimeout(() => void run(() => api.updateMap(cid, note.id, { grid: next })), 400);
  };

  const selected = map?.pins.find((p) => p.id === selectedId) ?? null;
  const selectedToken = map?.tokens.find((x) => x.id === selectedTokenId) ?? null;
  const cell = !map || map.grid.type === 'none' ? 70 : (grid ?? map.grid).size;
  const pinColor = (p: MapPinDTO) => p.color ?? (p.noteTypeKey ? types.byKey.get(p.noteTypeKey)?.color : undefined) ?? '#e8b04a';
  const labelOf = (p: MapPinDTO) => p.label || (p.noteTitle ? pinLabelFromTitle(p.noteTitle) : '?');
  const inv = 1 / view.scale;
  const showPanel = gm && (panel === 'grid' || panel === 'fog' || panel === 'measure' || selected || selectedToken);
  const gridScene = grid;

  if (!url) {
    return (
      <div className="map-empty card">
        <p className="muted">{t('map.noImage')}</p>
        {gm && (
          <ImagePicker
            cid={cid}
            value=""
            onChange={(v) => {
              if (!v) return;
              void api.patchFrontmatter(cid, note.id, { image: v }).then((res) => qc.setQueryData(['note', cid, note.id], res));
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className={classNames('map-view', props.fullscreen && 'fullscreen', props.className)}>
      <div className="map-toolbar">
        {gm && (
          <>
            <button className={classNames(tool === 'select' && 'on')} onClick={() => setTool('select')}>
              ✋ {t('map.select')}
            </button>
            <button className={classNames(tool === 'pin' && 'on')} onClick={() => setTool('pin')} title={t('map.addPinHint')}>
              📍 {t('map.addPin')}
            </button>
            <button className={classNames(tool === 'token' && 'on')} onClick={() => setTool('token')} title={t('map.addTokenHint')}>
              ● {t('map.addToken')}
            </button>
            <button
              className={classNames(tool === 'measure' && 'on')}
              onClick={() => {
                setTool((x) => (x === 'measure' ? 'select' : 'measure'));
                setPanel((p) => (p === 'measure' ? null : 'measure'));
                setMeasure(null);
              }}
            >
              📏 {t('map.measure')}
            </button>
            <button
              className={classNames(tool === 'fog' && 'on')}
              onClick={() => {
                setTool((x) => (x === 'fog' ? 'select' : 'fog'));
                setPanel((p) => (p === 'fog' ? null : 'fog'));
              }}
            >
              ☁ {t('map.fog')}
            </button>
            <button className={classNames(panel === 'grid' && 'on')} onClick={() => setPanel((p) => (p === 'grid' ? null : 'grid'))}>
              # {t('map.grid')}
            </button>
          </>
        )}
        <button onClick={fit}>⤢ {t('map.fit')}</button>
        <span className="muted small">{Math.round(view.scale * 100)} %</span>
        {gm && (
          <button
            className={classNames(screen.data?.mapNoteId === note.id && 'on')}
            onClick={() => void showToPlayers()}
            title={t('map.showToPlayersHint')}
          >
            📺 {screen.data?.mapNoteId === note.id ? t('map.shownToPlayers') : t('map.showToPlayers')}
          </button>
        )}
        {screenMsg && <span className="small map-screen-msg">{screenMsg}</span>}
        <span className="spacer" />
        {props.fullscreen ? (
          <Link className="button" to={`/c/${cid}/n/${note.id}`}>
            ← {t('map.backToNote')}
          </Link>
        ) : (
          <Link className="button" to={`/c/${cid}/map/${note.id}`}>
            ⛶ {t('map.fullscreen')}
          </Link>
        )}
      </div>
      {error && <div className="error small">{error}</div>}
      <div className="map-body">
        <div ref={wrapRef} className={classNames('map-canvas', tool !== 'select' && 'placing')}>
          {tool === 'pin' && <div className="map-hint small">{t('map.addPinHint')}</div>}
          {tool === 'token' && <div className="map-hint small">{t('map.addTokenHint')}</div>}
          {tool === 'measure' && <div className="map-hint small">{t('map.measureDragHint')}</div>}
          {tool === 'fog' && <div className="map-hint small">{fogMode.hide ? t('map.fogHideHint') : t('map.fogRevealHint')}</div>}
          {failed && <p className="error pad">{t('common.error')}: {url}</p>}
          {size.width > 0 && img && (
            <Stage
              ref={stageRef}
              width={size.width}
              height={size.height}
              x={view.x}
              y={view.y}
              scaleX={view.scale}
              scaleY={view.scale}
              draggable={tool === 'select'}
              onWheel={onWheel}
              onDragStart={() => setDraft(null)}
              // Position laufend übernehmen, damit ein erneutes Rendern (z. B. Hover) die Karte nicht zurücksetzt
              onDragMove={(e) => {
                if (e.target === e.target.getStage()) setView((v) => ({ ...v, x: e.target.x(), y: e.target.y() }));
              }}
              onDragEnd={(e) => {
                if (e.target === e.target.getStage()) setView((v) => ({ ...v, x: e.target.x(), y: e.target.y() }));
              }}
              onClick={onStageClick}
              onTap={onStageClick}
              onMouseDown={onFogDown}
              onTouchStart={onFogDown}
              onMouseMove={onFogMove}
              onTouchMove={onFogMove}
              onMouseUp={onFogUp}
              onTouchEnd={onFogUp}
            >
              <Layer listening={tool !== 'select'}>
                <KImage image={img} name="map-image" />
                {gridScene && gridScene.type !== 'none' && (
                  <Shape
                    listening={false}
                    stroke={gridScene.color}
                    opacity={gridScene.opacity}
                    strokeWidth={1}
                    strokeScaleEnabled={false}
                    sceneFunc={(ctx, shape) => {
                      drawGrid(ctx, gridScene, img.width, img.height);
                      ctx.strokeShape(shape);
                    }}
                  />
                )}
              </Layer>
              {fog && (fog.enabled || fogDraft) && <FogLayer fog={fog} draft={fogDraft} width={img.width} height={img.height} gm={gm} />}
              <Layer>
                {map?.templates.map((tp) => (
                  <TemplateNode
                    key={tp.id}
                    template={tp}
                    grid={grid ?? map.grid}
                    scale={view.scale}
                    selected={tp.id === selectedTemplateId}
                    onSelect={
                      gm && (tool === 'select' || tool === 'measure')
                        ? () => {
                            setSelectedTemplateId(tp.id);
                            setPanel('measure');
                          }
                        : undefined
                    }
                  />
                ))}
                {measure && map && measureKind !== 'ruler' && !measure.done && (
                  <TemplateNode template={{ kind: measureKind, x: measure.a.x, y: measure.a.y, x2: measure.b.x, y2: measure.b.y, color: templateColor }} grid={grid ?? map.grid} scale={view.scale} />
                )}
                {measure && map && measureKind === 'ruler' && <RulerNode a={measure.a} b={measure.b} grid={grid ?? map.grid} scale={view.scale} />}
              </Layer>
              <Layer>
                {map?.pins.map((p) => {
                  const lbl = labelOf(p);
                  const short = lbl.length <= 5;
                  // Pille, die mit der Beschriftung wächst („12/13“)
                  const w = short ? Math.max(PIN_R * 2, 10 + lbl.length * 7.5) : PIN_R * 2;
                  return (
                    <Group
                      key={p.id}
                      x={p.x}
                      y={p.y}
                      scaleX={inv}
                      scaleY={inv}
                      draggable={gm && tool === 'select'}
                      onClick={(e) => {
                        e.cancelBubble = true;
                        setSelectedId(p.id);
                        setSelectedTokenId(null);
                        setPanel(null);
                      }}
                      onTap={(e) => {
                        e.cancelBubble = true;
                        setSelectedId(p.id);
                      }}
                      onMouseEnter={(e) => {
                        const c = e.target.getStage()?.container();
                        if (c) c.style.cursor = 'pointer';
                        const pos = e.target.getStage()?.getPointerPosition();
                        if (pos) setHover({ pin: p, sx: pos.x, sy: pos.y });
                      }}
                      onMouseLeave={(e) => {
                        const c = e.target.getStage()?.container();
                        if (c) c.style.cursor = '';
                        setHover(null);
                      }}
                      onDragStart={() => setHover(null)}
                      onDragEnd={(e) => {
                        e.cancelBubble = true;
                        const x = e.target.x();
                        const y = e.target.y();
                        void run(() => api.updatePin(cid, note.id, p.id, { x, y }));
                      }}
                    >
                      <Rect
                        x={-w / 2}
                        y={-PIN_R}
                        width={w}
                        height={PIN_R * 2}
                        cornerRadius={PIN_R}
                        fill={pinColor(p)}
                        stroke={p.id === selectedId ? '#fff8e1' : '#1b1208'}
                        strokeWidth={p.id === selectedId ? 3 : 2}
                        dash={p.visibility === 'dm' ? [4, 3] : undefined}
                        shadowColor="#000"
                        shadowBlur={6}
                        shadowOpacity={0.6}
                      />
                      {short ? (
                        <Text text={lbl} fontSize={12} fontStyle="bold" fill="#1b1208" width={w} height={PIN_R * 2} offsetX={w / 2} offsetY={PIN_R} align="center" verticalAlign="middle" wrap="none" />
                      ) : (
                        <Text text={lbl} fontSize={13} fontStyle="bold" fill="#fff8e1" stroke="#1b1208" strokeWidth={3} fillAfterStrokeEnabled x={PIN_R + 4} y={-7} />
                      )}
                    </Group>
                  );
                })}
              </Layer>
              <Layer>
                {map?.tokens
                  .filter((tk) => gm || !tk.hidden)
                  .map((tk) => (
                    <TokenNode
                      key={tk.id}
                      token={tk}
                      grid={grid ?? map.grid}
                      cell={cell}
                      scale={view.scale}
                      selected={tk.id === selectedTokenId || (!!props.highlightCombatantId && tk.combatantId === props.highlightCombatantId)}
                      draggable={gm && tool === 'select'}
                      onSelect={() => {
                        setSelectedTokenId(tk.id);
                        setSelectedId(null);
                        setPanel(null);
                        props.onTokenSelect?.(tk);
                      }}
                      onMove={(pos) => void run(() => api.updateToken(cid, note.id, tk.id, pos))}
                    />
                  ))}
              </Layer>
            </Stage>
          )}
          {hover && !draft && (
            <div className="map-tooltip" style={{ left: hover.sx + 14, top: hover.sy + 10 }}>
              {hover.pin.noteTitle ?? hover.pin.label}
            </div>
          )}
          {draft && (
            <div className="map-draft" style={{ left: Math.min(draft.sx, size.width - 300), top: Math.min(draft.sy + 12, size.height - 60) }}>
              {tool === 'token' ? (
                <Combobox
                  value={draftText}
                  onChange={setDraftText}
                  options={creatureOptions}
                  autoFocus
                  limit={20}
                  placeholder={t('map.tokenName')}
                  onPick={(o) => createToken(o.title, o.key, o.note.typeKey)}
                  onEnter={() => draftText.trim() && createToken(draftText.trim(), null)}
                />
              ) : (
                <Combobox
                  value={draftText}
                  onChange={setDraftText}
                  options={noteOptions}
                  autoFocus
                  limit={20}
                  placeholder={t('map.pinNote')}
                  onPick={(o) => createPin(o.key, '')}
                  onEnter={() => draftText.trim() && createPin(null, draftText.trim())}
                />
              )}
            </div>
          )}
        </div>
        {showPanel && (
          <aside className="map-panel">
            {panel === 'measure' && map ? (
              <MeasureSettings
                kind={measureKind}
                color={templateColor}
                count={map.templates.length}
                selected={!!selectedTemplateId}
                onKind={(k) => {
                  setMeasureKind(k);
                  setTool('measure');
                  setMeasure(null);
                }}
                onColor={setTemplateColor}
                onDeleteSelected={() => {
                  saveTemplates(map.templates.filter((x) => x.id !== selectedTemplateId));
                  setSelectedTemplateId(null);
                }}
                onClear={() => {
                  saveTemplates([]);
                  setSelectedTemplateId(null);
                }}
              />
            ) : panel === 'fog' && fog && img ? (
              <FogSettings
                fog={fog}
                mode={fogMode}
                brush={brush}
                onMode={(m) => {
                  setFogMode(m);
                  setTool('fog');
                }}
                onBrush={setBrush}
                onChange={changeFog}
                imageSize={{ width: img.width, height: img.height }}
              />
            ) : panel === 'grid' && grid ? (
              <GridSettings grid={grid} onChange={changeGrid} />
            ) : selectedToken ? (
              <TokenPanel
                key={selectedToken.id}
                cid={cid}
                token={selectedToken}
                onPatch={(patch) => void run(() => api.updateToken(cid, note.id, selectedToken.id, patch))}
                onDelete={() => {
                  setSelectedTokenId(null);
                  void run(() => api.deleteToken(cid, note.id, selectedToken.id));
                }}
              />
            ) : selected ? (
              <PinPanel
                key={selected.id}
                cid={cid}
                pin={selected}
                onPatch={(patch) => void run(() => api.updatePin(cid, note.id, selected.id, patch))}
                onDelete={() => {
                  setSelectedId(null);
                  void run(() => api.deletePin(cid, note.id, selected.id));
                }}
              />
            ) : null}
          </aside>
        )}
      </div>
    </div>
  );
}
